import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PageMeta from '../components/PageMeta';
import Navbar from '../components/Navbar';
import Hero from '../components/Hero';
import InceptumProductNav from '../components/InceptumProductNav';
import GameInfo from '../components/GameInfo';
import WhatsInTheBox from '../components/WhatsInTheBox';
import HowToPlay from '../components/HowToPlay';
import Creators from '../components/Creators';
import Footer from '../components/Footer';
import InceptumLandingControls from '../components/InceptumLandingControls';
import InstagramStoryLanding from '../components/InstagramStoryLanding';
import { clarityEvent, claritySet, gaEvent } from '../analytics';
import useInstagramExperiment from '../hooks/useInstagramExperiment';
import styles from './InceptumPage.module.css';

export default function InceptumPage() {
  const location = useLocation();
  const experiment = useInstagramExperiment(location.search);
  const trackedTreatmentRef = useRef(null);
  const [storyWaitlistVisible, setStoryWaitlistVisible] = useState(false);

  useEffect(() => {
    if (!experiment.isExperiment || !experiment.treatment) return;
    if (trackedTreatmentRef.current === experiment.treatment) return;
    trackedTreatmentRef.current = experiment.treatment;

    const audience = experiment.audience || (experiment.isQa ? 'qa' : 'paid_instagram');
    claritySet('experiment', experiment.experimentName);
    claritySet('treatment', experiment.treatment);
    claritySet('audience', audience);
    clarityEvent('ig_experiment_assigned');
    gaEvent('experiment_impression', {
      experiment_id: experiment.experimentName,
      variant_id: experiment.treatment,
      audience,
    });
  }, [experiment]);

  useEffect(() => {
    const hash = location.hash ? location.hash.replace('#', '') : null;
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location]);

  const exploreTarget = experiment.treatment === 'd' ? 'components' :
    experiment.treatment === 'c' ? 'feature-core-challenge' : 'about';
  const isProductProofFirst = experiment.treatment === 'd';
  const isLazyTreatment = experiment.treatment?.startsWith('e');

  return (
    <>
      <PageMeta
        title="INCEPTUM: Dawn of Civilizations - Strategy Board Game"
        description="INCEPTUM: Dawn of Civilizations is a non-combat civilization strategy board game for 2–4 players built on timing, asymmetry, and meaningful trade-offs."
        path="/inceptum"
        ogTitle="INCEPTUM: Dawn of Civilizations"
        ogDescription="Lead your civilization from the Ancient Age, through the Medieval Age, to the dawn of modern civilization. Expand your city, research technologies, race for Wonders, and shape your path through timing and trade-offs."
      >
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://stratmechstudios.com/' },
              { '@type': 'ListItem', position: 2, name: 'INCEPTUM', item: 'https://stratmechstudios.com/inceptum' },
            ],
          })}
        </script>
      </PageMeta>
      <Navbar
        experimentQa={experiment.isQa ? {
          enabled: true,
          treatment: experiment.treatment,
          treatments: experiment.treatments,
          onChange: experiment.setQaTreatment,
        } : null}
      />
      <div
        className={styles.inceptumThemeShell}
        data-ig-treatment={experiment.treatment || undefined}
      >
        <main className="inceptum-product-page">
          {isLazyTreatment ? (
            <InstagramStoryLanding
              treatment={experiment.treatment}
              onWaitlistVisibilityChange={setStoryWaitlistVisible}
            />
          ) : (
            <Hero treatment={experiment.treatment} />
          )}
          <div className={`${styles.inceptumContentRegion} ${isProductProofFirst ? styles.productProofFirst : ''}`}>
            <InceptumProductNav />
            <GameInfo
              coreFirst={experiment.treatment === 'c'}
              hideFacts={experiment.treatment === 'b'}
            />
            <WhatsInTheBox visualFirst={isProductProofFirst} />
            <HowToPlay />
            {/* <Creators /> - commented out; may restore later */}
          </div>
        </main>
        <Footer variant="inceptum" />
      </div>
      <InceptumLandingControls
        experiment={experiment}
        exploreTarget={exploreTarget}
        storyWaitlistVisible={storyWaitlistVisible}
      />
    </>
  );
}
